import { FC } from "react"
import { Area, AreaChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"

import { useTranslation } from "react-i18next"

import { LoadingSpinner } from "components/general/LoadingSpinner"

import { useTimezone } from "hooks/useTimezone"

import { formatFiatCurrency } from "util/formatter"

import { ChartTooltipContent } from "./ChartTooltipContent"
import { StatOption } from "./statsConfig"

type Granularity = "year" | "month" | "day" | "hour"

interface Props {
  darkMode: boolean
  loading: boolean
  spinnerColor: string

  data: any[] | undefined
  granularity: Granularity

  yAxisWidth: number
  selectedStats: string[]
  options: StatOption[]
  currencyStats: Set<string>
}

export const StatsAreaChart: FC<Props> = ({
  darkMode,
  loading,
  spinnerColor,
  data,
  granularity,
  yAxisWidth,
  selectedStats,
  options,
  currencyStats
}) => {
  const { formatCustom } = useTimezone()
  const { t } = useTranslation("translation", { useSuspense: false })

  return (
    <div className="relative h-[300px]">
      {loading && (
        <div className="absolute top-0 left-0 right-0 bottom-20 flex items-center justify-center z-10">
          <LoadingSpinner size={36} color={spinnerColor} />
        </div>
      )}

      <ResponsiveContainer width="100%" height={300}>
        <AreaChart data={data} margin={{ top: 10, right: 30, left: 10, bottom: 10 }}>
          <CartesianGrid stroke={darkMode ? "rgba(255,255,255,0.1)" : "#E5E7EB"} />
          <XAxis
            tickLine={false}
            tickMargin={18}
            stroke={darkMode ? "white" : "#374151"}
            dataKey={granularity === "year" ? "year" : "period"}
            tickFormatter={(tick) => {
              if (granularity === "day") return formatCustom(tick, { day: "2-digit", month: "short" })
              if (granularity === "hour") return formatCustom(tick, { hour: "2-digit", minute: "2-digit" })
              if (granularity === "month") return formatCustom(tick, { month: "short", year: "numeric" })
              return tick
            }}
          />
          <YAxis
            width={yAxisWidth}
            tickLine={false}
            tickMargin={32}
            stroke={darkMode ? "white" : "#374151"}
            tickFormatter={(value) => {
              const selectedTypes = selectedStats.map((val) => options.find((opt) => opt.value === val)?.type || "count")
              const allAreCurrency = selectedTypes.every((type) => type === "currency")
              return allAreCurrency ? formatFiatCurrency(value) : value
            }}
          />
          <Tooltip content={<ChartTooltipContent granularity={granularity} options={options} currencyStats={currencyStats} />} />

          <Legend wrapperStyle={{ paddingTop: 30 }} />
          {selectedStats.map((statKey) => {
            const stat = options.find((s) => s.value === statKey)
            return (
              <Area
                key={statKey}
                type="monotone"
                dataKey={statKey}
                name={t(stat?.label || statKey)}
                stroke={stat?.color}
                strokeWidth={3}
                fill={stat?.color}
                fillOpacity={darkMode ? 0.15 : 0.1}
                dot={false}
                activeDot={false}
              />
            )
          })}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  )
}
